import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, ExternalLink, Pencil, FileText, Sparkles } from 'lucide-react';
import { BTS_URL } from '../constants';

const BehindTheScenes: React.FC = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Placeholders for prompts as requested
  const stylePrompt = "Create a high-energy, visual year-end wrap-up in the style of 'Spotify Wrapped.' Use a bold, neon-gradient color palette (purples, greens, and pinks). Title Card: The video must start with a title card that says '2025 BEACHHOUSE WHATSWRAPPED: PRESENTED BY SKINNY Will & SKINNY D' featuring two animated rapper personas. Structure: > 1. Start with a fast-paced 'Top 10 Points' countdown based on my Highlight Photos and 2025 Chat logs. 2. Each point should have a large 'Card' at the top with a punchy title. 3. Include 3 'Year Stats' (e.g., 'Most Active Chat Month' or 'Top Emoji'). 4. End with 2 'Personality Archetypes' for the user. Tone: The narration should be rhythmic and hype, like a radio host or a hype-man, introducing each segment as if Skinny D and Skinny Will are hosting the show.";
  
  const personalizedPrompt = "Create a high-energy, visual year-end wrap-up in the style of 'Spotify Wrapped.' Use a bold, neon-gradient color palette (purples, greens, and pinks). Title Card: The video must start with a title card that says '2025 BEACHHOUSE WHATSWRAPPED: PRESENTED BY SKINNY Will & SKINNY D' featuring two animated rapper personas. Structure: > 1. Start with a fast-paced 'Top 10 Points' countdown based on my Highlight Photos and 2025 Chat logs. 2. Each point should have a large 'Card' at the top with a punchy title. 3. Include 3 'Year Stats' (e.g., 'Most Active Chat Month' or 'Top Emoji'). 4. End with 2 'Personality Archetypes' for the user. Tone: The narration should be rhythmic and hype, like a radio host or a hype-man, introducing each segment as if Skinny D and Skinny Will are hosting the show.";

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="min-h-screen pt-32 pb-48 px-6 bg-black">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-6xl md:text-8xl font-black font-montserrat mb-4 tracking-tighter uppercase">
            Dev <span className="text-green-500 italic">Log</span>
          </h1>
          <p className="text-lg text-white/40 font-bold uppercase tracking-widest italic">The AI Workflow behind BH25</p>
        </motion.div>

        {/* NotebookLM Action Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12 p-8 rounded-[2.5rem] bg-gradient-to-br from-zinc-900 to-black border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div>
            <h2 className="text-2xl font-black mb-2 tracking-tight">Access the Brain</h2>
            <p className="text-white/40 text-sm font-medium">Open the NotebookLM workspace to begin the generation process.</p>
          </div>
          <a 
            href={BTS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-8 py-4 bg-white text-black rounded-full font-black text-xs uppercase tracking-widest hover:scale-105 transition-transform"
          >
            Open NotebookLM <ExternalLink size={14} />
          </a>
        </motion.div>

        {/* Workflow Section */}
        <motion.section 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <h3 className="text-[10px] font-black uppercase tracking-[0.5em] text-green-500 mb-8 px-4">The Generation Workflow</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: <Pencil size={20} />, title: "Step 1", desc: "Click on Video Summary (Pencil Icon)" },
              { icon: <FileText size={20} />, title: "Step 2", desc: "Select 'Brief' from options" },
              { icon: <Sparkles size={20} />, title: "Step 3", desc: "Paste Style & Personalized Prompts" }
            ].map((step, i) => (
              <div key={i} className="p-6 rounded-3xl bg-zinc-900/50 border border-white/5 flex flex-col gap-4">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60">
                  {step.icon}
                </div>
                <div>
                  <h4 className="font-black text-xs uppercase tracking-widest mb-1">{step.title}</h4>
                  <p className="text-white/40 text-sm font-medium leading-tight">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Prompts Section */}
        <div className="space-y-16">
          {/* Style Prompt Box */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4 px-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-white/60">Style Prompt (~800 chars)</h3>
              <button 
                onClick={() => handleCopy(stylePrompt, 'style')}
                className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-green-500 bg-green-500/10 px-4 py-2 rounded-full border border-green-500/20"
              >
                {copiedId === 'style' ? <Check size={14} /> : <Copy size={14} />}
                {copiedId === 'style' ? 'Copied' : 'Copy Style'}
              </button>
            </div>
            <div className="p-8 rounded-[2.5rem] bg-zinc-900 border border-white/5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-green-500/50"></div>
              <p className="text-white/80 text-sm leading-relaxed font-mono whitespace-pre-wrap min-h-[150px]">
                {stylePrompt}
              </p>
            </div>
          </motion.div>

          {/* Personalized Prompt Box */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-4 px-4">
              <h3 className="text-xs font-black uppercase tracking-widest text-white/60">Personalized Prompt (~1500 chars)</h3>
              <button 
                onClick={() => handleCopy(personalizedPrompt, 'personalized')}
                className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-green-500 bg-green-500/10 px-4 py-2 rounded-full border border-green-500/20"
              >
                {copiedId === 'personalized' ? <Check size={14} /> : <Copy size={14} />}
                {copiedId === 'personalized' ? 'Copied' : 'Copy Custom'}
              </button>
            </div>
            <div className="p-8 rounded-[2.5rem] bg-zinc-900 border border-white/5 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/50"></div>
              <p className="text-white/80 text-sm leading-relaxed font-mono whitespace-pre-wrap min-h-[300px]">
                {personalizedPrompt}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BehindTheScenes;